import * as PIXI from 'pixi.js';
import * as PrefabManager from '../PrefabManager';
import * as emitterManager from '../../utils/EmitterManager';
import * as AudioManager from '../../utils/AudioManager';
import * as PrefabBuilder from '../../utils/PrefabBuilder';
import * as PhysicsParticleEmitter from '../../utils/PhysicsParticleEmitter';
import { disableCulling } from '../../utils/PIXICuller';
import { gameRandom } from '../../utils/GameRandom';
import { normalizePI } from '../../b2Editor/utils/extramath';
import { Settings } from '../../Settings';
import {
	game
} from "../../Game";

const STATE_IDLE = 0;
const STATE_OPENING = 1;
const STATE_ARMED = 2;
const STATE_CLOSING = 3;

const OPEN_TIME = 450;
const CLOSE_TIME = 700;
const LOSE_TARGET_TIME = 2000;
const FIRE_GRACE_TIME = 400;
const MAX_AIM = 35 * Math.PI / 180;
const AIM_SPEED = 0.004;
const SWEEP_ANGLE = 0.09;
const SWEEP_PERIOD = 2600;
const SCAN_ANGLE = 0.12;
const BULLET_SPREAD = 0.035;
const BULLET_RANGE_MULTIPLIER = 1.25;
const HEADSHOT_MULTIPLIER = 2;
const BREAK_IMPULSE = 40;
const EXPLOSION_BREAK_POWER = 1.5;
const LEG_SEPARATION = 0.3;
const LEG_SNAP_FRAMES = 30;
const LEG_JOINTS = ['leg_left_back_joint', 'leg_left_front_joint', 'leg_right_back_joint', 'leg_right_front_joint'];

// Measured from the armed layout, relative to the centre of the body art (pixels).
const OPEN_POSES = {
	cover_back: {x: -54.389, y: -1.799, rotation: 0},
	cover_front: {x: 67.611, y: 0.206, rotation: 0},
	gun_back: {x: -43.953, y: -56.137, rotation: 0},
	gun_front: {x: -22.233, y: -51.7, rotation: 0},
};
const EYE_OFFSET = {x: -31, y: 0.3};
const GUN_NAMES = ['gun_front', 'gun_back'];
// Where the broken halves line up with the body art (pixels from its centre).
const BROKEN_LEFT_OFFSET = {x: -9, y: 0};
const BROKEN_RIGHT_OFFSET = {x: 9, y: -9};
const DEBRIS = [
	{name: 'TurretBrokenLeft', texture: 'Turret_Body_Broken_10000', density: 0.1, vertices: [{x:-1.117,y:-0.917},{x:-0.683,y:-2.95},{x:-0.217,y:-3.683},{x:0.683,y:-3.417},{x:1.083,y:2.883},{x:0.317,y:3.783},{x:-0.783,y:2.65},{x:-1.117,y:0.85}]},
	{name: 'TurretBrokenRight', texture: 'Turret_Body_Broken_20000', density: 0.1, vertices: [{x:-0.883,y:-1.317},{x:-0.45,y:-3.083},{x:0.083,y:-3.25},{x:0.783,y:-1.383},{x:0.883,y:0.75},{x:0.683,y:2.483},{x:0.383,y:3.283},{x:-0.55,y:3.15}]},
	{name: 'TurretMechanism', texture: 'Turret_Body_Broken_30000', density: 0.5, vertices: [{x:-0.517,y:-0.117},{x:-0.283,y:-0.35},{x:0.183,y:-0.417},{x:0.483,y:-0.183},{x:0.517,y:0.283},{x:0.283,y:0.35},{x:-0.117,y:0.383},{x:-0.517,y:0.383}]},
	{name: 'TurretCircuitBoard', texture: 'Turret_Body_Broken_40000', density: 0.5, vertices: [{x:-0.183,y:-0.417},{x:-0.083,y:-0.417},{x:0.55,y:0.05},{x:0.55,y:0.183},{x:0.217,y:0.383},{x:0.117,y:0.383},{x:-0.383,y:0.183},{x:-0.65,y:-0.05}]},
	{name: 'TurretCoverBack', texture: 'Turret_Cover_Back0000', density: 0.1, vertices: [{x:-0.917,y:-1.95},{x:-0.45,y:-2.917},{x:-0.017,y:-2.717},{x:1.05,y:-0.617},{x:1.05,y:0.583},{x:-0.017,y:2.683},{x:-0.583,y:2.783},{x:-1.017,y:1.35}]},
	{name: 'TurretCoverFront', texture: 'Turret_Cover_Front0000', density: 0.1, vertices: [{x:-0.75,y:-0.95},{x:-0.15,y:-2.917},{x:0.35,y:-2.617},{x:0.65,y:-1.517},{x:0.717,y:0.917},{x:0.317,y:2.65},{x:-0.083,y:2.917},{x:-0.55,y:2.117}]},
	{name: 'TurretGunBack', texture: 'Turret_Gun_Back0000', density: 0.3, vertices: [{x:-1.783,y:-0.133},{x:-1.717,y:-0.2},{x:-0.783,y:-0.3},{x:1.617,y:-0.3},{x:1.783,y:0.033},{x:1.517,y:0.3},{x:-0.717,y:0.3},{x:-1.75,y:0.2}]},
	{name: 'TurretGunFront', texture: 'Turret_Gun_Front0000', density: 0.3, vertices: [{x:-1.733,y:-0.2},{x:-0.767,y:-0.3},{x:1.6,y:-0.3},{x:1.767,y:-0.133},{x:1.767,y:0.1},{x:1.5,y:0.3},{x:-0.7,y:0.3},{x:-1.733,y:0.2}]},
];

const vec1 = new Box2D.b2Vec2();
const vec2 = new Box2D.b2Vec2();

const rayCallback = Object.assign(new Box2D.JSRayCastCallback(), {
	ignoreKey: null,
	hit: false,
	body: null,
	x: 0,
	y: 0,
	reset(ignoreKey) {
		this.ignoreKey = ignoreKey;
		this.hit = false;
		this.body = null;
	},
	ReportFixture(fixture_p, point_p, normal_p, fraction) {
		const fixture = Box2D.wrapPointer(fixture_p, Box2D.b2Fixture);
		if(fixture.IsSensor()) return -1;
		if(fixture.GetFilterData().get_categoryBits() === game.editor.MASKBIT_NOTHING) return -1;
		const body = fixture.GetBody();
		if(body.isPhysicsCamera || body.isPortalGhost || body.isPortalShadow) return -1;
		const sprite = body.mySprite;
		if(sprite?.isPhysicsParticleSprite || sprite?.data?.type === game.editor.object_TRIGGER) return -1;
		if(sprite?.data?.prefabInstanceName === this.ignoreKey) return -1;
		const point = Box2D.wrapPointer(point_p, Box2D.b2Vec2);
		this.hit = true;
		this.body = body;
		this.x = point.get_x();
		this.y = point.get_y();
		return fraction;
	},
});

const clamp01 = value => Math.max(0, Math.min(1, value));
const ease = value => value * value * (3 - 2 * value);
const approach = (value, target, maxStep) => value + Math.max(-maxStep, Math.min(maxStep, target - value));

class Turret extends PrefabManager.weaponPrefab {
	static settings = Object.assign({}, PrefabManager.weaponPrefab.settings, {
		"isFlipped": false,
		"active": true,
		"isDestructible": true,
		"laserRange": 25,
		"fireRate": 12,
		"damage": 3,
		"bulletForce": 3,
	});
	static settingsOptions = Object.assign({}, PrefabManager.weaponPrefab.settingsOptions, {
		"isFlipped": false,
		"active": true,
		"isDestructible": true,
		"laserRange": {
			min: 5,
			max: 100,
			step: 1
		},
		"fireRate": {
			min: 1,
			max: 30,
			step: 1
		},
		"damage": {
			min: 0,
			max: 30,
			step: 0.5
		},
		"bulletForce": {
			min: 0,
			max: 30,
			step: 0.5
		},
	});

	constructor(target) {
		super(target);
		this.isTurret = true;
		this.flipped = false;
		// Turrets placed before a setting existed would otherwise read it as undefined.
		this.prefabObject.settings = Object.assign({}, Turret.settings, this.prefabObject.settings);
	}

	postConstructor() {
		// Runs before isFlipped is applied, so everything here is in unflipped body space.
		this.group = this.lookupObject.body.myTexture;
		const groupData = this.group.data;
		this.groupRotation = -groupData.textureAngleOffset;
		this.groupOrigin = {
			x: groupData.texturePositionOffsetLength * Math.cos(-groupData.texturePositionOffsetAngle),
			y: groupData.texturePositionOffsetLength * Math.sin(-groupData.texturePositionOffsetAngle),
		};

		this.closedPoses = {};
		groupData.graphicObjects.forEach(graphicObject => {
			this.closedPoses[graphicObject[5]] = {x: graphicObject[1], y: graphicObject[2], rotation: graphicObject[3]};
		});
		const art = this.closedPoses.body_texture;

		this.covers = ['cover_back', 'cover_front'].map(name => ({
			name,
			closed: this.closedPoses[name],
			open: {x: art.x + OPEN_POSES[name].x, y: art.y + OPEN_POSES[name].y},
		}));

		this.guns = GUN_NAMES.map(name => {
			const closed = this.closedPoses[name];
			const open = {x: art.x + OPEN_POSES[name].x, y: art.y + OPEN_POSES[name].y, rotation: OPEN_POSES[name].rotation};
			const delta = normalizePI(open.rotation - closed.rotation);
			const c = Math.cos(delta);
			const s = Math.sin(delta);
			const bx = open.x - (c * closed.x - s * closed.y);
			const by = open.y - (s * closed.x + c * closed.y);
			const a = 1 - c;
			const det = a * a + s * s;
			const index = groupData.graphicObjects.findIndex(graphicObject => graphicObject[5] === name);
			const width = this.group.children[index].texture.width;
			return {
				name,
				closed,
				delta,
				pivot: {x: (a * bx - s * by) / det, y: (s * bx + a * by) / det},
				muzzle: {x: -width / 2, y: 0},
				aim: 0,
				kick: 0,
			};
		});

		this.eye = this.groupToBody(art.x + EYE_OFFSET.x, art.y + EYE_OFFSET.y);
		this.resolveParts();
	}

	init() {
		super.init();
		this.body = this.lookupObject.body;

		this.active = this.prefabObject.settings.active;
		this.laserRange = this.prefabObject.settings.laserRange;
		this.fireInterval = 1000 / this.prefabObject.settings.fireRate;
		this.damage = this.prefabObject.settings.damage;
		this.bulletForce = this.prefabObject.settings.bulletForce;

		this.state = STATE_IDLE;
		this.openProgress = 0;
		this.time = 0;
		this.lostTime = LOSE_TARGET_TIME;
		this.eyeAim = 0;
		this.targetX = 0;
		this.targetY = 0;
		this.bellyRider = null;
		this.fireTimer = 0;
		this.nextGun = 0;
		this.laserVisible = false;
		this.laserHit = false;
		this.eyeWorld = {x: 0, y: 0};
		this.laserEnd = {x: 0, y: 0};
		this.tracers = [];
		this.flashes = [];
		this.broken = false;
		this.breakQueued = false;
		this.legSnapFrames = {};

		this.sparks = emitterManager.getLoopingEmitter("sparksMetal", this.body, this.body.GetPosition(), 0);
		this.sparks.emit = false;
		const sparkSpread = (this.sparks.maxStartRotation - this.sparks.minStartRotation) / 2;
		this.sparks.minStartRotation = -sparkSpread;
		this.sparks.maxStartRotation = sparkSpread;

		this.effects = new PIXI.Container();
		this.laserGraphics = new PIXI.Graphics();
		this.laserGraphics.blendMode = 'add';
		this.fxGraphics = new PIXI.Graphics();
		this.fxGraphics.blendMode = 'add';
		this.effects.addChild(this.laserGraphics, this.fxGraphics);
		disableCulling(this.effects);
		game.editor.textures.addChildAt(this.effects, this.topDisplayIndex() + 1);

		AudioManager.preloadSFX('servo-move');
	}

	set(property, value) {
		super.set(property, value);
		switch (property) {
			case 'isFlipped':
				this.flip(value);
				break;
			case 'active':
				this.active = value;
				break;
		}
	}

	flip(flipped) {
		if(this.flipped != flipped){
			this.flipped = flipped;
			game.editor.mirrorPrefab(this, 'body');
		}
	}

	topDisplayIndex() {
		const container = game.editor.textures;
		let top = -1;
		const consider = display => {
			if(display && display.parent === container) top = Math.max(top, container.getChildIndex(display));
		};
		this.lookupObject._bodies.forEach(body => {
			consider(body.mySprite);
			consider(body.myTexture);
		});
		this.lookupObject._textures.forEach(consider);
		return top;
	}

	resolveParts() {
		this.parts = {};
		this.group.data.graphicObjects.forEach((graphicObject, index) => {
			this.parts[graphicObject[5]] = this.group.children[index];
		});
	}

	groupToBody(x, y) {
		const c = Math.cos(this.groupRotation);
		const s = Math.sin(this.groupRotation);
		return {
			x: (this.groupOrigin.x + c * x - s * y) / Settings.PTM,
			y: (this.groupOrigin.y + s * x + c * y) / Settings.PTM,
		};
	}

	bodyToWorld(local, out) {
		const position = this.body.GetPosition();
		const angle = this.body.GetAngle();
		const c = Math.cos(angle);
		const s = Math.sin(angle);
		const x = this.flipped ? -local.x : local.x;
		out.x = position.x + c * x - s * local.y;
		out.y = position.y + s * x + c * local.y;
		return out;
	}

	worldToBody(x, y) {
		const position = this.body.GetPosition();
		const angle = this.body.GetAngle();
		const c = Math.cos(angle);
		const s = Math.sin(angle);
		const dx = x - position.x;
		const dy = y - position.y;
		const localX = c * dx + s * dy;
		return {x: this.flipped ? -localX : localX, y: -s * dx + c * dy};
	}

	// Barrels and the eye look along -x in unflipped body space at rotation 0.
	bodyDirectionToWorld(rotation) {
		const angle = this.body.GetAngle();
		const c = Math.cos(angle);
		const s = Math.sin(angle);
		const x = this.flipped ? Math.cos(rotation) : -Math.cos(rotation);
		const y = -Math.sin(rotation);
		return {x: c * x - s * y, y: s * x + c * y};
	}

	elevationTo(from) {
		const target = this.worldToBody(this.targetX, this.targetY);
		const elevation = normalizePI(Math.atan2(from.y - target.y, from.x - target.x) - this.groupRotation);
		return Math.max(-MAX_AIM, Math.min(MAX_AIM, elevation));
	}

	castRay(fromX, fromY, toX, toY) {
		vec1.Set(fromX, fromY);
		vec2.Set(toX, toY);
		rayCallback.reset(this.prefabObject.key);
		game.world.RayCast(rayCallback, vec1, vec2);
		return rayCallback.hit;
	}

	gunPose(gun, gunOpen) {
		const angle = (gun.delta + gun.aim) * gunOpen;
		const c = Math.cos(angle);
		const s = Math.sin(angle);
		const dx = gun.closed.x - gun.pivot.x;
		const dy = gun.closed.y - gun.pivot.y;
		return {
			x: gun.pivot.x + c * dx - s * dy,
			y: gun.pivot.y + s * dx + c * dy,
			rotation: gun.closed.rotation + angle,
		};
	}

	gunOpenAmount() {
		return ease(clamp01((this.openProgress - 0.3) / 0.7));
	}

	update() {
		if(this.destroyed) return;
		super.update();
		const deltaTime = game.editor.deltaTime;
		this.time += deltaTime;

		if(this.breakQueued && !this.broken) this.breakApart();
		if(!this.broken){
			this.checkLegJoints();
			this.updateTargeting(deltaTime);
			this.updateState(deltaTime);
			this.updateParts(deltaTime);
		}
		this.drawEffects(deltaTime);
		this.sparks.update(deltaTime * 0.001);
	}

	initContactListener() {
		super.initContactListener();
		const self = this;
		this.contactListener.PostSolve = function (contact, impulse) {
			if(self.broken || self.breakQueued || !self.prefabObject.settings.isDestructible) return;
			const fixtureA = contact.GetFixtureA();
			const fixtureB = contact.GetFixtureB();
			const bodyA = fixtureA.GetBody();
			const bodyB = fixtureB.GetBody();
			if(bodyA !== self.body && bodyB !== self.body) return;
			const other = bodyA === self.body ? bodyB : bodyA;
			if(other.mySprite?.data?.prefabInstanceName === self.prefabObject.key) return;
			if(fixtureA.GetDensity() === 0.001 || fixtureB.GetDensity() === 0.001) return;
			let force = 0;
			for (let j = 0, count = impulse.get_count(); j < count; j++){
				force = Math.max(force, impulse.get_normalImpulses(j));
			}
			if(force > BREAK_IMPULSE) self.breakQueued = true;
		}
	}

	explosionHit(powerRate) {
		if(this.prefabObject.settings.isDestructible && powerRate > EXPLOSION_BREAK_POWER) this.breakQueued = true;
	}

	queueBreak() {
		this.breakQueued = true;
	}

	checkLegJoints() {
		LEG_JOINTS.forEach(name => {
			const joint = this.lookupObject[name];
			if(!joint || joint.destroyed) return;
			const anchorA = joint.GetAnchorA();
			const ax = anchorA.x;
			const ay = anchorA.y;
			const anchorB = joint.GetAnchorB();
			const dx = anchorB.x - ax;
			const dy = anchorB.y - ay;
			if(dx * dx + dy * dy > LEG_SEPARATION * LEG_SEPARATION){
				this.legSnapFrames[name] = (this.legSnapFrames[name] || 0) + 1;
				if(this.legSnapFrames[name] >= LEG_SNAP_FRAMES) this.snapLeg(joint, ax, ay);
			}else{
				this.legSnapFrames[name] = 0;
			}
		});
	}

	snapLeg(joint, x, y) {
		const leg = joint.GetBodyA() === this.body ? joint.GetBodyB() : joint.GetBodyA();
		const springs = this.lookupObject._joints.filter(other => other !== joint && !other.destroyed &&
			other.GetType() === Box2D.e_distanceJoint && (other.GetBodyA() === leg || other.GetBodyB() === leg));
		game.editor.deleteObjects([joint, ...springs]);

		this.sparks.spawnPos.set(x * Settings.PTM, y * Settings.PTM);
		this.sparks.rotation = -90;
		this.sparks.playOnce();
		AudioManager.playSFX(['snap1', 'snap2'], 0.3, 1.0 + 0.2 * Math.random(), {x, y});
	}

	breakApart() {
		if(this.broken || this.destroyed) return;
		this.broken = true;
		this.breakQueued = false;
		this.laserVisible = false;

		const body = this.body;
		const angle = body.GetAngle();
		const position = {x: body.GetPosition().x, y: body.GetPosition().y};
		const velocity = {x: body.GetLinearVelocity().x, y: body.GetLinearVelocity().y};
		const angularVelocity = body.GetAngularVelocity();
		const gravity = game.world.GetGravity();
		const gravityX = gravity.x;
		const gravityY = gravity.y;
		const gravityLength = Math.sqrt(gravityX * gravityX + gravityY * gravityY);
		const up = gravityLength > 0 ? {x: -gravityX / gravityLength, y: -gravityY / gravityLength} : {x: Math.sin(angle), y: -Math.cos(angle)};

		const art = this.closedPoses.body_texture;
		const coverOpen = ease(clamp01(this.openProgress / 0.6));
		const gunOpen = this.gunOpenAmount();
		const pieces = [
			{name: 'TurretBrokenLeft', x: art.x + BROKEN_LEFT_OFFSET.x, y: art.y + BROKEN_LEFT_OFFSET.y, rotation: art.rotation, speed: 3, spin: 1},
			{name: 'TurretBrokenRight', x: art.x + BROKEN_RIGHT_OFFSET.x, y: art.y + BROKEN_RIGHT_OFFSET.y, rotation: art.rotation, speed: 3, spin: 1},
			{name: 'TurretMechanism', x: art.x + 30, y: art.y, rotation: art.rotation, speed: 12, spin: 12},
			{name: 'TurretCircuitBoard', x: art.x, y: art.y - 30, rotation: art.rotation, speed: 12, spin: 12},
		];
		this.covers.forEach(cover => pieces.push({
			name: cover.name === 'cover_back' ? 'TurretCoverBack' : 'TurretCoverFront',
			x: cover.closed.x + (cover.open.x - cover.closed.x) * coverOpen,
			y: cover.closed.y + (cover.open.y - cover.closed.y) * coverOpen,
			rotation: cover.closed.rotation,
			speed: 6,
			spin: 3,
		}));
		this.guns.forEach(gun => {
			const pose = this.gunPose(gun, gunOpen);
			pieces.push({name: gun.name === 'gun_back' ? 'TurretGunBack' : 'TurretGunFront', x: pose.x, y: pose.y, rotation: pose.rotation, speed: 8, spin: 6});
		});

		const center = this.bodyToWorld(this.groupToBody(art.x, art.y), {x: 0, y: 0});
		pieces.forEach(piece => {
			piece.world = this.bodyToWorld(this.groupToBody(piece.x, piece.y), {x: 0, y: 0});
			piece.angle = angle + (this.flipped ? -1 : 1) * (this.groupRotation + piece.rotation);
		});

		game.editor.deleteObjects([body]);
		game.editor.deleteObjects(this.lookupObject._joints.filter(joint => !joint.destroyed));

		pieces.forEach(piece => {
			const world = piece.world;
			vec1.Set(world.x, world.y);
			const { lookupObject, prefabClass } = PrefabBuilder.generatePrefab(vec1, piece.angle * game.editor.RAD2DEG, piece.name, true);
			if(this.flipped) game.editor.mirrorPrefab(prefabClass, 'body');

			let dx = world.x - center.x;
			let dy = world.y - center.y;
			const distance = Math.sqrt(dx * dx + dy * dy);
			if(distance > 0.001){
				dx /= distance;
				dy /= distance;
			}
			const side = gameRandom() * 2 - 1;
			dx += up.x * 0.8 + up.y * side * 0.4;
			dy += up.y * 0.8 - up.x * side * 0.4;
			const length = Math.sqrt(dx * dx + dy * dy) || 1;
			const push = piece.speed * (0.7 + gameRandom() * 0.6);

			const pieceBody = lookupObject._bodies[0];
			vec2.Set(
				velocity.x - angularVelocity * (world.y - position.y) + dx / length * push,
				velocity.y + angularVelocity * (world.x - position.x) + dy / length * push,
			);
			pieceBody.SetLinearVelocity(vec2);
			pieceBody.SetAngularVelocity(angularVelocity + (gameRandom() * 2 - 1) * piece.spin);
		});

		PhysicsParticleEmitter.emit(['Cardboard_1', 'Cardboard_2', 'Cardboard_3'], center, 24, 5, 20, false, [0xFFFFFF, 0xE6E6E6, 0x1E1E1E, 0x333333]);

		this.sparks.spawnPos.set(center.x * Settings.PTM, center.y * Settings.PTM);
		this.sparks.rotation = Math.atan2(up.y, up.x) * game.editor.RAD2DEG;
		this.sparks.playOnce();

		AudioManager.playSFX('metal-impact2', 0.4, 1.0, center);
		AudioManager.playSFX('impact-heavy1', 0.3, 0.9 + 0.2 * Math.random(), center);
	}

	updateTargeting(deltaTime) {
		this.laserVisible = this.active;
		if(!this.active){
			this.lostTime += deltaTime;
			return;
		}

		const searching = this.state === STATE_IDLE || this.state === STATE_CLOSING;
		if(searching) this.bellyRider = null;
		let desired;
		if(searching){
			desired = SWEEP_ANGLE * Math.sin(this.time * Math.PI * 2 / SWEEP_PERIOD) + SWEEP_ANGLE * 0.25 * Math.sin(this.time / 370);
		}else{
			desired = this.elevationTo(this.eye);
			if(this.lostTime > 0) desired += SCAN_ANGLE * Math.sin(this.time / 110);
		}
		this.eyeAim = approach(this.eyeAim, desired, AIM_SPEED * deltaTime);

		const eye = this.bodyToWorld(this.eye, this.eyeWorld);
		const direction = this.bodyDirectionToWorld(this.groupRotation + this.eyeAim);
		const endX = eye.x + direction.x * this.laserRange;
		const endY = eye.y + direction.y * this.laserRange;

		let seen = false;
		if(this.castRay(eye.x, eye.y, endX, endY)){
			this.laserHit = true;
			this.laserEnd.x = rayCallback.x;
			this.laserEnd.y = rayCallback.y;
			const body = rayCallback.body;
			if(body.GetType() === Box2D.b2_dynamicBody){
				const center = this.aimBodyFor(body).GetWorldCenter();
				this.targetX = center.x;
				this.targetY = center.y;
				seen = true;
			}
		}else{
			this.laserHit = false;
			this.laserEnd.x = endX;
			this.laserEnd.y = endY;
		}

		if(seen){
			this.lostTime = 0;
			if(searching){
				if(this.state === STATE_IDLE) AudioManager.playSFX('drone-beep', 0.25, 1.6, this.body.GetPosition());
				AudioManager.playSFX('servo-move', 0.3, 1.2, this.body.GetPosition());
				this.state = STATE_OPENING;
			}
		}else{
			this.lostTime += deltaTime;
		}
	}

	// A vehicle part sends the aim to its rider's belly, and the rider's own limbs keep it there until the turret searches again.
	aimBodyFor(body) {
		const rider = this.riderOf(body);
		if(rider) this.bellyRider = rider;
		else if(game.editor.retrieveSubClassFromBody(body) !== this.bellyRider) this.bellyRider = null;
		const belly = this.bellyRider?.lookupObject.belly;
		return belly && !belly.destroyed ? belly : body;
	}

	riderOf(body) {
		const vehicle = game.editor.retrieveClassFromBody(body);
		let rider = null;
		if(vehicle?.isVehicle) rider = vehicle.character;
		else if(body.isVehiclePart && game.character?.vehicleParts?.includes(body)) rider = game.character;
		if(!rider?.attachedToVehicle || game.editor.retrieveSubClassFromBody(body) === rider) return null;
		return rider;
	}

	updateState(deltaTime) {
		switch (this.state) {
			case STATE_OPENING:
				this.openProgress = Math.min(1, this.openProgress + deltaTime / OPEN_TIME);
				if(this.openProgress === 1){
					this.state = STATE_ARMED;
					this.fireTimer = this.fireInterval;
				}
				break;
			case STATE_ARMED:
				if(!this.active || this.lostTime > LOSE_TARGET_TIME){
					this.state = STATE_CLOSING;
					AudioManager.playSFX('servo-move', 0.3, 1.0, this.body.GetPosition());
					break;
				}
				if(this.lostTime < FIRE_GRACE_TIME){
					this.fireTimer += deltaTime;
					while(this.fireTimer >= this.fireInterval){
						this.fireTimer -= this.fireInterval;
						this.shoot(this.guns[this.nextGun]);
						this.nextGun = 1 - this.nextGun;
					}
				}else{
					this.fireTimer = Math.min(this.fireTimer + deltaTime, this.fireInterval);
				}
				break;
			case STATE_CLOSING:
				this.openProgress = Math.max(0, this.openProgress - deltaTime / CLOSE_TIME);
				if(this.openProgress === 0) this.state = STATE_IDLE;
				break;
		}
		if(this.state === STATE_OPENING && !this.active) this.state = STATE_CLOSING;

		const tracking = this.active && (this.state === STATE_OPENING || this.state === STATE_ARMED);
		this.guns.forEach(gun => {
			const pivot = this.groupToBody(gun.pivot.x, gun.pivot.y);
			const desired = tracking ? this.elevationTo(pivot) : 0;
			gun.aim = approach(gun.aim, desired, AIM_SPEED * deltaTime);
		});
	}

	shoot(gun) {
		const pose = this.gunPose(gun, this.gunOpenAmount());
		const c = Math.cos(pose.rotation);
		const s = Math.sin(pose.rotation);
		const muzzle = this.bodyToWorld(this.groupToBody(
			pose.x + c * gun.muzzle.x - s * gun.muzzle.y,
			pose.y + s * gun.muzzle.x + c * gun.muzzle.y,
		), {x: 0, y: 0});

		const spread = (gameRandom() * 2 - 1) * BULLET_SPREAD;
		const direction = this.bodyDirectionToWorld(this.groupRotation + pose.rotation + spread);
		const range = this.laserRange * BULLET_RANGE_MULTIPLIER;
		let endX = muzzle.x + direction.x * range;
		let endY = muzzle.y + direction.y * range;

		const hit = this.castRay(muzzle.x, muzzle.y, endX, endY);
		if(hit){
			endX = rayCallback.x;
			endY = rayCallback.y;
			this.hitBody(rayCallback.body, endX, endY, direction);
		}

		const angle = Math.atan2(direction.y, direction.x);
		this.tracers.push({x0: muzzle.x * Settings.PTM, y0: muzzle.y * Settings.PTM, x1: endX * Settings.PTM, y1: endY * Settings.PTM, age: 0, hit});
		this.flashes.push({x: muzzle.x * Settings.PTM, y: muzzle.y * Settings.PTM, angle, age: 0, size: 0.8 + Math.random() * 0.4});
		gun.kick = 6;

		AudioManager.playSFX('cannon', 0.08, 2.6 + Math.random() * 0.4, muzzle);
	}

	hitBody(body, x, y, direction) {
		const angleDegrees = Math.atan2(direction.y, direction.x) * game.editor.RAD2DEG;

		if(body.GetType() === Box2D.b2_dynamicBody && this.bulletForce > 0){
			vec1.Set(direction.x * this.bulletForce, direction.y * this.bulletForce);
			vec2.Set(x, y);
			body.ApplyLinearImpulse(vec1, vec2, true);
		}

		const bodyClass = game.editor.retrieveSubClassFromBody(body);
		if(body.isFlesh && bodyClass?.dealDamage && this.damage > 0){
			const headshot = body.mySprite?.data?.refName === 'head';
			bodyClass.dealDamage(this.damage * (headshot ? HEADSHOT_MULTIPLIER : 1));
		}

		const prefabClass = game.editor.retrieveClassFromBody(body);
		if(prefabClass?.isExplosive && !prefabClass.exploded && !prefabClass.destroyed) prefabClass.explode();

		const point = {x, y};
		if(body.isFlesh){
			emitterManager.playOnceEmitter("blood", body, point, angleDegrees);
			game.editor.queueDecalToBody(body, point, "Decal.png", true, 0.5);
		}else{
			this.sparks.spawnPos.set(x * Settings.PTM, y * Settings.PTM);
			this.sparks.rotation = angleDegrees + 180;
			this.sparks.playOnce();
		}
	}

	updateParts(deltaTime) {
		if(this.parts.body_texture?.parent !== this.group) this.resolveParts();

		const coverOpen = ease(clamp01(this.openProgress / 0.6));
		this.covers.forEach(cover => {
			const part = this.parts[cover.name];
			part.x = cover.closed.x + (cover.open.x - cover.closed.x) * coverOpen;
			part.y = cover.closed.y + (cover.open.y - cover.closed.y) * coverOpen;
		});

		const gunOpen = this.gunOpenAmount();
		this.guns.forEach(gun => {
			gun.kick = Math.max(0, gun.kick - deltaTime * 0.15);
			const pose = this.gunPose(gun, gunOpen);
			const part = this.parts[gun.name];
			part.x = pose.x + Math.cos(pose.rotation) * gun.kick;
			part.y = pose.y + Math.sin(pose.rotation) * gun.kick;
			part.rotation = pose.rotation;
		});
	}

	drawEffects(deltaTime) {
		const PTM = Settings.PTM;
		const laser = this.laserGraphics;
		laser.clear();
		if(this.laserVisible){
			const armed = this.state === STATE_OPENING || this.state === STATE_ARMED;
			const flicker = 0.85 + Math.random() * 0.15;
			const x0 = this.eyeWorld.x * PTM;
			const y0 = this.eyeWorld.y * PTM;
			const x1 = this.laserEnd.x * PTM;
			const y1 = this.laserEnd.y * PTM;
			laser.moveTo(x0, y0).lineTo(x1, y1).stroke({width: armed ? 7 : 5, color: 0xff1a1a, alpha: (armed ? 0.3 : 0.18) * flicker});
			laser.moveTo(x0, y0).lineTo(x1, y1).stroke({width: armed ? 2 : 1.5, color: 0xff6060, alpha: 0.9 * flicker});
			laser.circle(x0, y0, armed ? 9 : 7).fill({color: 0xff2020, alpha: 0.3 * flicker});
			if(this.laserHit){
				laser.circle(x1, y1, 6).fill({color: 0xff2020, alpha: 0.35 * flicker});
				laser.circle(x1, y1, 2.2).fill({color: 0xffb0b0, alpha: 0.9});
			}
		}

		const fx = this.fxGraphics;
		fx.clear();

		const tracerSpeed = 5;
		const tracerLength = 90;
		const impactTime = 60;
		for(let i = this.tracers.length - 1; i >= 0; i--){
			const tracer = this.tracers[i];
			tracer.age += deltaTime;
			const dx = tracer.x1 - tracer.x0;
			const dy = tracer.y1 - tracer.y0;
			const distance = Math.sqrt(dx * dx + dy * dy) || 1;
			const travelled = tracer.age * tracerSpeed;
			const sinceImpact = tracer.age - distance / tracerSpeed;
			const tail = Math.max(0, travelled - tracerLength);
			if(tail >= distance && (!tracer.hit || sinceImpact >= impactTime)){
				this.tracers.splice(i, 1);
				continue;
			}
			if(tail < distance){
				const head = Math.min(distance, travelled);
				const tx = tracer.x0 + dx * tail / distance;
				const ty = tracer.y0 + dy * tail / distance;
				const hx = tracer.x0 + dx * head / distance;
				const hy = tracer.y0 + dy * head / distance;
				fx.moveTo(tx, ty).lineTo(hx, hy).stroke({width: 6, color: 0x3fa9ff, alpha: 0.35});
				fx.moveTo(tx, ty).lineTo(hx, hy).stroke({width: 2, color: 0xe8f8ff, alpha: 0.95});
			}
			if(tracer.hit && sinceImpact >= 0 && sinceImpact < impactTime){
				const fade = 1 - sinceImpact / impactTime;
				fx.circle(tracer.x1, tracer.y1, 7).fill({color: 0x8fd4ff, alpha: 0.5 * fade});
				fx.circle(tracer.x1, tracer.y1, 3).fill({color: 0xffffff, alpha: 0.9 * fade});
			}
		}

		const flashTime = 50;
		for(let i = this.flashes.length - 1; i >= 0; i--){
			const flash = this.flashes[i];
			flash.age += deltaTime;
			if(flash.age > flashTime){
				this.flashes.splice(i, 1);
				continue;
			}
			const fade = 1 - flash.age / flashTime;
			const size = flash.size;
			const c = Math.cos(flash.angle);
			const s = Math.sin(flash.angle);
			const point = (along, across) => [flash.x + c * along - s * across, flash.y + s * along + c * across];
			fx.circle(flash.x, flash.y, 16 * size).fill({color: 0x4fb8ff, alpha: 0.35 * fade});
			fx.poly([...point(-4, -7 * size), ...point(40 * size, 0), ...point(-4, 7 * size)]).fill({color: 0x9fe2ff, alpha: 0.8 * fade});
			fx.poly([...point(2, -9 * size), ...point(14 * size, -20 * size), ...point(10 * size, -3 * size)]).fill({color: 0x9fe2ff, alpha: 0.6 * fade});
			fx.poly([...point(2, 9 * size), ...point(14 * size, 20 * size), ...point(10 * size, 3 * size)]).fill({color: 0x9fe2ff, alpha: 0.6 * fade});
			fx.poly([...point(-2, -3.5 * size), ...point(24 * size, 0), ...point(-2, 3.5 * size)]).fill({color: 0xffffff, alpha: fade});
			fx.circle(flash.x, flash.y, 6 * size).fill({color: 0xffffff, alpha: fade});
		}
	}

	destroy() {
		if(this.destroyed) return;
		if(this.sparks){
			emitterManager.destroyEmitter(this.sparks);
			delete this.sparks;
		}
		if(this.effects){
			this.effects.destroy({children: true});
			delete this.effects;
		}
		super.destroy();
	}
}

PrefabManager.prefabLibrary.Turret = {
	json: '{"objects":[[0,-0.0776,-3.8683,0,"turret","body",0,["#999999"],["#000"],[0],false,true,[[[{"x":-1.2089,"y":-0.6733},{"x":-1.0422,"y":-2.1733},{"x":-0.6422,"y":-3.2733},{"x":-0.0756,"y":-3.8733},{"x":0.2911,"y":-3.8733},{"x":0.9244,"y":-3.1067},{"x":1.3578,"y":-1.1067},{"x":1.3911,"y":0.66},{"x":1.2244,"y":1.96},{"x":0.6578,"y":3.2933},{"x":0.2244,"y":3.66},{"x":-0.2089,"y":3.56},{"x":-0.6089,"y":3.06},{"x":-1.0756,"y":1.5267},{"x":-1.2089,"y":0.36}]]],[0.1],[0],[0],"",[0],true,false,false,[0.5],[0.2],false,true,false,false,1,false],[0,-1.2776,1.0651,0.2793,"turret","leg_left_back",1,["#999999"],["#000"],[0],false,true,[[[{"x":-0.2062,"y":-2.2831},{"x":-0.2062,"y":1.8835},{"x":0.1634,"y":2.3463},{"x":0.2187,"y":-2.3173}]]],[1],[0],[0],"",[0],true,false,false,[0.5],[0.2],false,true,false,false,1,false],[1,-38.3381,37.0903,0.2793,"","",2,"Turret_Leg_Down_Back0000",1,5.1371,-1.2936,0,false,"#FFFFFF",1,1,1,0,0,0,true],[0,-1.1443,1.1317,0.2618,"turret","leg_left_front",3,["#999999"],["#000"],[0],false,true,[[[{"x":-0.2062,"y":-2.2831},{"x":-0.2062,"y":1.8835},{"x":0.1762,"y":2.3693},{"x":0.2187,"y":-2.3173}]]],[1],[0],[0],"",[0],true,false,false,[0.5],[0.2],false,true,false,false,1,false],[7,6.3052,-125.9052,0,"","",4,[[1,-22.6331,5.8533,0,"turret","cover_back",86,"Turret_Cover_Back0000",null,null,null,null,false,"#FFFFFF",1,1,1,0,0,0,true],[1,-23.1971,-14.4843,-1.5708,"turret","gun_back",87,"Turret_Gun_Back0000",null,null,null,null,false,"#FFFFFF",1,1,1,0,0,0,true],[1,-1.3999,7.653,0,"turret","body_texture",88,"Turret_Body0000",null,null,null,null,false,"#FFFFFF",1,1,1,0,0,0,true],[1,23.3665,-6.1426,-1.5708,"turret","gun_front",89,"Turret_Gun_Front0000",null,null,null,null,false,"#FFFFFF",1,1,1,0,0,0,true],[1,23.8635,7.1208,0,"turret","cover_front",90,"Turret_Cover_Front0000",null,null,null,null,false,"#FFFFFF",1,1,1,0,0,0,true]],0,13.1015,0.8514,0,1,0,0,0,true,false,[]],[0,1.2557,1.1984,-0.3316,"turret","leg_right_back",5,["#999999"],["#000"],[0],false,true,[[[{"x":0.2062,"y":-2.2831},{"x":0.2062,"y":1.8835},{"x":-0.1663,"y":2.2637},{"x":-0.2187,"y":-2.3173}]]],[1],[0],[0],"",[0],true,false,false,[0.5],[0.2],false,true,false,false,1,false],[1,37.951,41.0817,-0.3316,"","",6,"Turret_Leg_Down_Back0000",5,5.1371,-1.848,0,false,"#FFFFFF",-1,1,1,0,0,0,true],[1,-33.7435,38.7732,0.2618,"","",7,"Turret_Leg_Down_Front0000",3,4.8573,-1.1883,0,false,"#FFFFFF",1,1,1,0,0,0,true],[0,1.2175,1.1568,-0.2618,"turret","leg_right_front",8,["#999999"],["#000"],[0],false,true,[[[{"x":0.2062,"y":-2.2831},{"x":0.2062,"y":1.8835},{"x":-0.1573,"y":2.2989},{"x":-0.2187,"y":-2.3173}]]],[1],[0],[0],"",[0],true,false,false,[0.5],[0.2],false,true,false,false,1,false],[1,35.9408,39.5262,-0.2618,"","",9,"Turret_Leg_Down_Front0000",8,4.8573,-1.9533,0,false,"#FFFFFF",-1,1,1,0,0,0,true],[2,-14.5414,-29.5768,0,"turret","leg_left_front_joint",10,0,3,0,false,false,1,10,true,45,-45,0,5,0,0,true],[2,16.048,-28.2468,0,"turret","leg_right_front_joint",11,0,8,0,false,false,1,10,true,45,-45,0,5,0,0,true],[2,11.6717,-29.0478,0,"turret","leg_right_back_joint",12,0,5,0,false,false,1,10,true,45,-45,0,5,0,0,true],[2,-19.3283,-30.0478,0,"turret","leg_left_back_joint",13,0,1,0,false,false,1,10,true,45,-45,0,5,0,0,true],[2,-0.8508,32.8904,0,"","",14,3,8,2,false,false,1,10,false,0,0,0.3,2,0,0,true],[2,-0.3283,32.9522,0,"","",15,1,5,2,false,false,1,10,false,0,0,0.3,2,0,0,true]]}',
	class: Turret,
	library: PrefabManager.LIBRARY_WEAPON,
}

DEBRIS.forEach(debris => {
	PrefabManager.prefabLibrary[debris.name] = {
		json: JSON.stringify({objects: [
			[0, 0, 0, 0, "turretDebris", "body", 0, ["#999999"], ["#000"], [0], false, true, [debris.vertices], [debris.density], 0, [0], "", [1]],
			[1, 0, 0, 0, "turretDebris", "texture", 1, debris.texture, 0, 0, 0, 0, false, "#FFFFFF", 1, 1, 1],
		]}),
		class: PrefabManager.basePrefab,
	};
});
